import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Edit2,
  Copy,
  Archive,
  Trash2,
  Play,
  ExternalLink,
  RefreshCw,
  Users,
  Calendar,
  Mic,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Quiz {
  id: number;
  title: string;
  description: string | null;
  hostName: string | null;
  venue: string | null;
  scheduledAt: string | null;
  joinCode: string;
  status: string;
  maxTeamSize: number | null;
  maxTeams: number | null;
  createdAt: string;
  teamCount?: number;
  activeSessionId?: number | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusBadge(status: string) {
  const map: Record<string, { label: string; class: string }> = {
    draft:     { label: "Draft",     class: "bg-gray-100 text-gray-700" },
    scheduled: { label: "Scheduled", class: "bg-blue-100 text-blue-700" },
    lobby:     { label: "Lobby",     class: "bg-yellow-100 text-yellow-700" },
    live:      { label: "Live",      class: "bg-green-100 text-green-700" },
    paused:    { label: "Paused",    class: "bg-orange-100 text-orange-700" },
    completed: { label: "Completed", class: "bg-purple-100 text-purple-700" },
    cancelled: { label: "Cancelled", class: "bg-red-100 text-red-700" },
    archived:  { label: "Archived",  class: "bg-gray-100 text-gray-500" },
  };
  const m = map[status] ?? { label: status, class: "bg-gray-100 text-gray-700" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${m.class}`}>
      {m.label}
    </span>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminQuizzes() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const token = localStorage.getItem("ayrshire_token");

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [archiveId, setArchiveId] = useState<number | null>(null);
  const [startId, setStartId] = useState<number | null>(null);

  const { data: quizzes = [], isLoading, refetch } = useQuery<Quiz[]>({
    queryKey: ["admin-quizzes"],
    enabled: user?.role === "admin",
    queryFn: () =>
      fetch("/api/admin/quiz", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
  });

  // Auto-refresh if any quiz is live or in lobby
  const hasLive = quizzes.some(q => q.status === "live" || q.status === "lobby");
  useEffect(() => {
    if (!hasLive) return;
    const id = setInterval(() => refetch(), 10_000);
    return () => clearInterval(id);
  }, [hasLive, refetch]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      fetch(`/api/admin/quiz/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => { if (!r.ok) throw new Error("Delete failed"); }),
    onSuccess: () => {
      toast({ title: "Quiz deleted" });
      qc.invalidateQueries({ queryKey: ["admin-quizzes"] });
      setDeleteId(null);
    },
    onError: () => toast({ title: "Delete failed", variant: "destructive" }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) =>
      fetch(`/api/admin/quiz/${id}/archive`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => { if (!r.ok) throw new Error("Archive failed"); }),
    onSuccess: () => {
      toast({ title: "Quiz archived" });
      qc.invalidateQueries({ queryKey: ["admin-quizzes"] });
      setArchiveId(null);
    },
    onError: () => toast({ title: "Archive failed", variant: "destructive" }),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: number) =>
      fetch(`/api/admin/quiz/${id}/duplicate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => { if (!r.ok) throw new Error("Duplicate failed"); return r.json(); }),
    onSuccess: () => {
      toast({ title: "Quiz duplicated" });
      qc.invalidateQueries({ queryKey: ["admin-quizzes"] });
    },
    onError: () => toast({ title: "Duplicate failed", variant: "destructive" }),
  });

  const startSessionMutation = useMutation({
    mutationFn: (quizId: number) =>
      fetch("/api/admin/quiz/sessions", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ quizId }),
      }).then(r => { if (!r.ok) throw new Error("Failed to start session"); return r.json(); }),
    onSuccess: (data: { id: number }) => {
      toast({ title: "Session started — lobby open" });
      qc.invalidateQueries({ queryKey: ["admin-quizzes"] });
      setStartId(null);
      // Navigate to host panel
      window.location.href = `/admin/quiz/host/${data.id}`;
    },
    onError: () => toast({ title: "Failed to start session", variant: "destructive" }),
  });

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const tabs = [
    { key: "active",    label: "Active",    filter: (q: Quiz) => q.status === "lobby" || q.status === "live" || q.status === "paused" },
    { key: "scheduled", label: "Scheduled", filter: (q: Quiz) => q.status === "scheduled" },
    { key: "draft",     label: "Draft",     filter: (q: Quiz) => q.status === "draft" },
    { key: "history",   label: "History",   filter: (q: Quiz) => q.status === "completed" || q.status === "cancelled" || q.status === "archived" },
  ];

  function QuizCard({ quiz }: { quiz: Quiz }) {
    const isLive = quiz.status === "lobby" || quiz.status === "live" || quiz.status === "paused";
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-base truncate">{quiz.title}</h3>
                {statusBadge(quiz.status)}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {quiz.hostName && (
                  <span className="flex items-center gap-1">
                    <Mic className="h-3.5 w-3.5" />
                    {quiz.hostName}
                  </span>
                )}
                {quiz.venue && <span>{quiz.venue}</span>}
                {quiz.scheduledAt && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(quiz.scheduledAt)}
                  </span>
                )}
                {quiz.joinCode && (
                  <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                    {quiz.joinCode}
                  </span>
                )}
                {(quiz.teamCount ?? 0) > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    {quiz.teamCount} teams
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {isLive && quiz.activeSessionId && (
                <Link href={`/admin/quiz/host/${quiz.activeSessionId}`}>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Host Panel
                  </Button>
                </Link>
              )}

              {(quiz.status === "draft" || quiz.status === "scheduled") && (
                <Button
                  size="sm"
                  className="gap-1"
                  onClick={() => setStartId(quiz.id)}
                >
                  <Play className="h-3.5 w-3.5" />
                  Start
                </Button>
              )}

              {quiz.status !== "archived" && (
                <Link href={`/admin/quiz/${quiz.id}/edit`}>
                  <Button size="sm" variant="outline" className="gap-1">
                    <Edit2 className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                </Link>
              )}

              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => duplicateMutation.mutate(quiz.id)}
                disabled={duplicateMutation.isPending}
              >
                <Copy className="h-3.5 w-3.5" />
                Copy
              </Button>

              {quiz.status !== "archived" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1 text-muted-foreground"
                  onClick={() => setArchiveId(quiz.id)}
                >
                  <Archive className="h-3.5 w-3.5" />
                  Archive
                </Button>
              )}

              <Button
                size="sm"
                variant="outline"
                className="gap-1 text-red-600 hover:text-red-700"
                onClick={() => setDeleteId(quiz.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <AdminLayout current="/admin/quiz">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary">Quiz Night</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Manage and host live quiz events</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
            <Link href="/admin/quiz/new">
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Create Quiz
              </Button>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="active">
          <TabsList>
            {tabs.map(t => {
              const count = quizzes.filter(t.filter).length;
              return (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                  {count > 0 && (
                    <span className="ml-1.5 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                      {count}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {tabs.map(t => {
            const filtered = quizzes.filter(t.filter);
            return (
              <TabsContent key={t.key} value={t.key} className="mt-4">
                {isLoading ? (
                  <div className="text-center py-12 text-muted-foreground">Loading quizzes...</div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No {t.label.toLowerCase()} quizzes
                    {t.key === "draft" && (
                      <p className="mt-2">
                        <Link href="/admin/quiz/new">
                          <Button variant="outline" size="sm" className="gap-1">
                            <Plus className="h-4 w-4" /> Create your first quiz
                          </Button>
                        </Link>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filtered.map(q => <QuizCard key={q.id} quiz={q} />)}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {/* Delete confirm */}
      <AlertDialog open={deleteId !== null} onOpenChange={open => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quiz?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the quiz and all its rounds, questions, and session data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive confirm */}
      <AlertDialog open={archiveId !== null} onOpenChange={open => !open && setArchiveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Quiz?</AlertDialogTitle>
            <AlertDialogDescription>
              The quiz will be archived and hidden from active lists. You can still view it in history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archiveId && archiveMutation.mutate(archiveId)}
              disabled={archiveMutation.isPending}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Start session confirm */}
      <AlertDialog open={startId !== null} onOpenChange={open => !open && setStartId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Open Lobby?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a live session and open the lobby so teams can join. You'll be taken to the host control panel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => startId && startSessionMutation.mutate(startId)}
              disabled={startSessionMutation.isPending}
            >
              {startSessionMutation.isPending ? "Opening..." : "Open Lobby"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
